/*
 * Copyright (C) 2016 Bilibili. All Rights Reserved.
 *
 * @author zheng qian <xqq@xqq.im>
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import EventEmitter from 'events';
import Log from '../utils/logger.js';
import Browser from '../utils/browser.js';
import PlayerEvents from './player-events.js';
import Transmuxer from '../core/transmuxer.js';
import TransmuxingEvents from '../core/transmuxing-events.js';
import MSEController from '../core/mse-controller.js';
import MSEEvents from '../core/mse-events.js';
import {ErrorTypes, ErrorDetails} from './player-errors.js';
import {createDefaultConfig} from '../config.js';
import {InvalidArgumentException, IllegalStateException} from '../utils/exception.js';

//Why use global variables? When calling createMediaElementSource twice,
//Google Chrome will throw this error message:
//
//  Uncaught DOMException: Failed to execute 'createMediaElementSource' on 'AudioContext': 
//  HTMLMediaElement already connected previously to a different MediaElementSourceNode.
//
//But, Firefox wouldn't, I think it is Chrome's bug : https://bugs.chromium.org/p/chromium/issues/detail?id=851310

//And some guys meet the same issue yet, like this:
//https://stackoverflow.com/questions/60620255/audio-context-breaks-when-switching-to-a-new-audio-source
let g_audioSource = null;
let g_audioCtx = new AudioContext();
let g_audioGain = g_audioCtx.createGain();
g_audioGain.connect(g_audioCtx.destination);

class FlvPlayer {

    constructor(mediaDataSource, config) {
        this.TAG = 'FlvPlayer';
        this._type = 'FlvPlayer';
        this._emitter = new EventEmitter();

        this._config = createDefaultConfig();
        if (typeof config === 'object') {
            Object.assign(this._config, config);
        }

        if (mediaDataSource.type.toLowerCase() !== 'flv') {
            throw new InvalidArgumentException('FlvPlayer requires an flv MediaDataSource input!');
        }

        if (mediaDataSource.isLive === true) {
            this._config.isLive = true;
        }

        this.e = {
            onvLoadedMetadata: this._onvLoadedMetadata.bind(this),
            onvSeeking: this._onvSeeking.bind(this),
            onvCanPlay: this._onvCanPlay.bind(this),
            onvStalled: this._onvStalled.bind(this),
            onvProgress: this._onvProgress.bind(this)
        };

        if (self.performance && self.performance.now) {
            this._now = self.performance.now.bind(self.performance);
        } else {
            this._now = Date.now;
        }

        Log.d(this.TAG, 'New FlvPlayer: ' + mediaDataSource.url);

        this._pendingSeekTime = null;  // in seconds
        this._requestSetTime = false;
        this._seekpointRecord = null;
        this._progressChecker = null;

        this._mediaDataSource = mediaDataSource;
        this._mediaElement = null;
        this._msectl = null;
        this._transmuxer = null;

        this._mseSourceOpened = false;
        this._hasPendingLoad = false;
        this._receivedCanPlay = false;

        this._mediaInfo = null;
        this._statisticsInfo = null;

        let chromeNeedIDRFix = (Browser.chrome &&
                               (Browser.version.major < 50 ||
                               (Browser.version.major === 50 && Browser.version.build < 2661)));
        this._alwaysSeekKeyframe = (chromeNeedIDRFix || Browser.msedge || Browser.msie) ? true : false;

        if (this._alwaysSeekKeyframe) {
            this._config.accurateSeek = false;
        }


        this._videoStateMonitorInterval = this._config.videoStateMonitorInterval;
        // for duration monitor
        this._enableDurationMonitor = this._config.enableDurationMonitor;
        this._curLatencyThreshold = this._config.lowLatencyThreshold;
        this._decreaseDurationStep = this._config.decreaseDurationStep;
        this._durationMonitor = null;
        // for video frozen monitor
        this._enableVideoFrozenMonitor = this._config.enableVideoFrozenMonitor;
        this._frozenTimesThreshold = this._config.frozenTimesThreshold;
        this._forzenTimes = 0;
        this._lastDecodedFrames = 0;

        //每个周期（M，毫秒）计算一下延迟追赶发生的次数
        // >= N次，认为调整次数太频繁
        // < N次，认为调整不频繁，下调延迟阈值 P（秒），但不能低于下限值
        this._latencyDetectInterval = this._config.latencyDetectInterval;   //M, 毫秒
        this._latencyDetectThreshold = this._config.latencyDetectThreshold; //N
        // this._latencyDetectIncreaseStep = this._config.latencyDetectIncreaseStep;   //K
        this._latencyDetectAdjustStep = this._config.latencyDetectAdjustStep;   //P 
        // this._latencyDetectAdjustFactor = this._config.latencyDetectAdjustFactor;  //增加 P 秒的时间间隔因子，即多少个_latencyDetectInterval才触发一次上调

        this._latencyDetectTimer = null;
        this._latencyAdjustCount = 0;
        this._latencyAdjustCountTotal = 0;
        // this._latencyNoAdjustCount = 0;
        // this._latencyAdjustFactor = 0;

        this._enableAudioGain = this._config.enableAudioGain;
        this._audioGainLevel = this._config.audioGainLevel > 0 ? this._config.audioGainLevel : 1;
        // this._audioCtx = new AudioContext();
        // this._gainNode = this._audioCtx.createGain();
        // this._gainNode.connect(this._audioCtx.destination);
        // this._gainNode.gain.value = this._audioGainLevel;
        // this._source = null;
        g_audioGain.gain.value = this._audioGainLevel;
    }

    destroy() {
        if (this._progressChecker != null) {
            window.clearInterval(this._progressChecker);
            this._progressChecker = null;
        }
        if (this._transmuxer) {
            this.unload();
        }
        if (this._mediaElement) {
            this.detachMediaElement();
        }
        this.e = null;
        this._mediaDataSource = null;

        if (this._emitter) {
            this._emitter.removeAllListeners();
            this._emitter = null;
        }

        // if (this._source != undefined) {
        //     this._source.disconnect(this._gainNode);
        // }
        
        // if (this._gainNode != undefined) {
        //     this._gainNode.disconnect(this._audioCtx.destination);
        // }

        // this._audioCtx = null;
    }

    on(event, listener) {
        if (event === PlayerEvents.MEDIA_INFO) {
            if (this._mediaInfo != null) {
                Promise.resolve().then(() => {
                    this._emitter.emit(PlayerEvents.MEDIA_INFO, this.mediaInfo);
                });
            }
        } else if (event === PlayerEvents.STATISTICS_INFO) {
            if (this._statisticsInfo != null) {
                Promise.resolve().then(() => {
                    this._emitter.emit(PlayerEvents.STATISTICS_INFO, this.statisticsInfo);
                });
            }
        }
        this._emitter.addListener(event, listener);
    }

    off(event, listener) {
        this._emitter.removeListener(event, listener);
    }

    attachMediaElement(mediaElement) {
        this._mediaElement = mediaElement;

        //see more events of video element from https://developer.mozilla.org/en-US/docs/Web/HTML/Element/video
        mediaElement.addEventListener('loadedmetadata', this.e.onvLoadedMetadata);
        mediaElement.addEventListener('seeking', this.e.onvSeeking);
        mediaElement.addEventListener('canplay', this.e.onvCanPlay);
        mediaElement.addEventListener('stalled', this.e.onvStalled);
        mediaElement.addEventListener('progress', this.e.onvProgress);

        this._msectl = new MSEController(this._config);

        this._msectl.on(MSEEvents.UPDATE_END, this._onmseUpdateEnd.bind(this));
        this._msectl.on(MSEEvents.BUFFER_FULL, this._onmseBufferFull.bind(this));
        this._msectl.on(MSEEvents.SOURCE_OPEN, () => {
            this._mseSourceOpened = true;
            if (this._hasPendingLoad) {
                this._hasPendingLoad = false;
                this.load();
            }
        });
        this._msectl.on(MSEEvents.SOURCE_END, () => {
            this._emitter.emit(PlayerEvents.MEDIA_SOURCE_ENDED);
        });
        this._msectl.on(MSEEvents.SOURCE_CLOSE, () => {
            this._mseSourceOpened = false;
            this._emitter.emit(PlayerEvents.MEDIA_SOURCE_CLOSE);
        });
        this._msectl.on(MSEEvents.ERROR, (info) => {
            this._emitter.emit(PlayerEvents.ERROR,
                               ErrorTypes.MEDIA_ERROR,
                               ErrorDetails.MEDIA_MSE_ERROR,
                               info
            );
        });

        this._msectl.attachMediaElement(mediaElement);

        if (this._pendingSeekTime != null) {
            try {
                mediaElement.currentTime = this._pendingSeekTime;
                this._pendingSeekTime = null;
            } catch (e) {
                // IE11 may throw InvalidStateError if readyState === 0
                // We can defer set currentTime operation after loadedmetadata
            }
        }
    }

    detachMediaElement() {
        if (this._mediaElement) {
            this._msectl.detachMediaElement();
            this._mediaElement.removeEventListener('loadedmetadata', this.e.onvLoadedMetadata);
            this._mediaElement.removeEventListener('seeking', this.e.onvSeeking);
            this._mediaElement.removeEventListener('canplay', this.e.onvCanPlay);
            this._mediaElement.removeEventListener('stalled', this.e.onvStalled);
            this._mediaElement.removeEventListener('progress', this.e.onvProgress);
            this._mediaElement = null;
        }
        if (this._msectl) {
            this._msectl.destroy();
            this._msectl = null;
        }
    }

    load() {
        if (!this._mediaElement) {
            throw new IllegalStateException('HTMLMediaElement must be attached before load()!');
        }
        if (this._transmuxer) {
            throw new IllegalStateException('FlvPlayer.load() has been called, please call unload() first!');
        }
        if (this._hasPendingLoad) {
            return;
        }

        if (this._config.deferLoadAfterSourceOpen && this._mseSourceOpened === false) {
            this._hasPendingLoad = true;
            return;
        }

        if (this._mediaElement.readyState > 0) {
            this._requestSetTime = true;
            // IE11 may throw InvalidStateError if readyState === 0
            this._mediaElement.currentTime = 0;
        }

        if (this._enableDurationMonitor && (this._durationMonitor == null)) {
            this._durationMonitor = self.setInterval(this._doDurationMonitor.bind(this), this._videoStateMonitorInterval);
            this._forzenTimes = 0;
            this._lastDecodedFrames = 0;

            this._latencyDetectTimer = self.setInterval(this._adjustDurationGap.bind(this), this._latencyDetectInterval);
        }

        this._transmuxer = new Transmuxer(this._mediaDataSource, this._config);

        this._transmuxer.on(TransmuxingEvents.INIT_SEGMENT, (type, is) => {
            this._msectl.appendInitSegment(is);
        });
        this._transmuxer.on(TransmuxingEvents.MEDIA_SEGMENT, (type, ms) => {
            this._msectl.appendMediaSegment(ms);

            // lazyLoad check
            if (this._config.lazyLoad && !this._config.isLive) {
                let currentTime = this._mediaElement.currentTime;
                if (ms.info.endDts >= (currentTime + this._config.lazyLoadMaxDuration) * 1000) {
                    if (this._progressChecker == null) {
                        Log.v(this.TAG, 'Maximum buffering duration exceeded, suspend transmuxing task');
                        this._suspendTransmuxer();
                    }
                }
            }
        });
        this._transmuxer.on(TransmuxingEvents.LOADING_COMPLETE, () => {
            this._msectl.endOfStream();
            this._emitter.emit(PlayerEvents.LOADING_COMPLETE);
        });
        this._transmuxer.on(TransmuxingEvents.RECOVERED_EARLY_EOF, () => {
            this._emitter.emit(PlayerEvents.RECOVERED_EARLY_EOF);
        });
        this._transmuxer.on(TransmuxingEvents.IO_ERROR, (detail, info) => {
            this._emitter.emit(PlayerEvents.ERROR, ErrorTypes.NETWORK_ERROR, detail, info);
        });
        this._transmuxer.on(TransmuxingEvents.DEMUX_ERROR, (detail, info) => {
            this._emitter.emit(PlayerEvents.ERROR, ErrorTypes.MEDIA_ERROR, detail, {code: -1, msg: info});
        });
        this._transmuxer.on(TransmuxingEvents.MEDIA_INFO, (mediaInfo) => {
            this._mediaInfo = mediaInfo;
            this._emitter.emit(PlayerEvents.MEDIA_INFO, Object.assign({}, mediaInfo));
        });
        this._transmuxer.on(TransmuxingEvents.METADATA_ARRIVED, (metadata) => {
            this._emitter.emit(PlayerEvents.METADATA_ARRIVED, metadata);
        });
        this._transmuxer.on(TransmuxingEvents.SCRIPTDATA_ARRIVED, (data) => {
            this._emitter.emit(PlayerEvents.SCRIPTDATA_ARRIVED, data);
        });
        this._transmuxer.on(TransmuxingEvents.STATISTICS_INFO, (statInfo) => {
            this._statisticsInfo = this._fillStatisticsInfo(statInfo);
            this._emitter.emit(PlayerEvents.STATISTICS_INFO, Object.assign({}, this._statisticsInfo));
        });
        this._transmuxer.on(TransmuxingEvents.RECOMMEND_SEEKPOINT, (milliseconds) => {
            if (this._mediaElement && !this._config.accurateSeek) {
                this._requestSetTime = true;
                this._mediaElement.currentTime = milliseconds / 1000;
            }
        });
        this._transmuxer.on(TransmuxingEvents.VIDEO_RESOLUTION_CHANGED, (video_info) => {
            this._emitter.emit(PlayerEvents.VIDEO_RESOLUTION_CHANGED, video_info);
        });

        this._transmuxer.open();
    }

    unload() {
        if (this._mediaElement) {
            this._mediaElement.pause();
        }
        if (this._msectl) {
            this._msectl.seek(0);
        }
        if (this._transmuxer) {
            this._transmuxer.close();
            this._transmuxer.destroy();
            this._transmuxer = null;
        }

        if (this._durationMonitor) {
            self.clearInterval(this._durationMonitor);
            this._durationMonitor = null;
        }

        if (this._latencyDetectTimer) {
            self.clearInterval(this._latencyDetectTimer);
            this._latencyDetectTimer = null;
            this._latencyAdjustCount = 0;

            Log.i(this.TAG, '--== Total latency adjust count : ' + this._latencyAdjustCountTotal);
            this._latencyAdjustCountTotal = 0;
        }

        this._forzenTimes = 0;
        this._lastDecodedFrames = 0;
    }

    play() {
        return this._mediaElement.play();
    }

    pause() {
        this._mediaElement.pause();
    }

    get type() {
        return this._type;
    }

    get buffered() {
        return this._mediaElement.buffered;
    }

    get duration() {
        return this._mediaElement.duration;
    }

    get volume() {
        return this._mediaElement.volume;
    }

    set volume(value) {
        this._mediaElement.volume = value;
    }

    get muted() {
        return this._mediaElement.muted;
    }

    set muted(muted) {
        this._mediaElement.muted = muted;
    }

    get currentTime() {
        if (this._mediaElement) {
            return this._mediaElement.currentTime;
        }
        return 0;
    }

    set currentTime(seconds) {
        if (this._mediaElement) {
            this._internalSeek(seconds);
        } else {
            this._pendingSeekTime = seconds;
        }
    }

    get mediaInfo() {
        return Object.assign({}, this._mediaInfo);
    }

    get statisticsInfo() {
        if (this._statisticsInfo == null) {
            this._statisticsInfo = {};
        }
        this._statisticsInfo = this._fillStatisticsInfo(this._statisticsInfo);
        return Object.assign({}, this._statisticsInfo);
    }

    _fillStatisticsInfo(statInfo) {
        statInfo.playerType = this._type;

        if (!(this._mediaElement instanceof HTMLVideoElement)) {
            return statInfo;
        }

        let hasQualityInfo = true;
        let decoded = 0;
        let dropped = 0;

        if (this._mediaElement.getVideoPlaybackQuality) {
            let quality = this._mediaElement.getVideoPlaybackQuality();
            decoded = quality.totalVideoFrames;
            dropped = quality.droppedVideoFrames;
        } else if (this._mediaElement.webkitDecodedFrameCount != undefined) {
            decoded = this._mediaElement.webkitDecodedFrameCount;
            dropped = this._mediaElement.webkitDroppedFrameCount;
        } else {
            hasQualityInfo = false;
        }

        if (hasQualityInfo) {
            statInfo.decodedFrames = decoded;
            statInfo.droppedFrames = dropped;
        }

        return statInfo;
    }

    enableVideoStateMonitor(enabled) {
        this._enableDurationMonitor = enabled;
        this._enableVideoFrozenMonitor = enabled;
    }

    _increaseLatency(second) {
        this._curLatencyThreshold += second;
        if (this._curLatencyThreshold > this._config.highLatencyThreshold) {
            this._curLatencyThreshold = this._config.highLatencyThreshold;
        }
        Log.i(this.TAG, 'latency +' + this._curLatencyThreshold);
    }

    _decreaseLatency(second) {
        this._curLatencyThreshold -= second;
        if (this._curLatencyThreshold < this._config.lowLatencyThreshold) {
            this._curLatencyThreshold = this._config.lowLatencyThreshold;
        }
        Log.i(this.TAG, 'latency -' + this._curLatencyThreshold);
    }

    _updateCurrentLatencyThreshold(second) {
        this._curLatencyThreshold = second;
        if (this._curLatencyThreshold < this._config.lowLatencyThreshold) {
            this._curLatencyThreshold = this._config.lowLatencyThreshold;
        }
        if (this._curLatencyThreshold > this._config.highLatencyThreshold) {
            this._curLatencyThreshold = this._config.highLatencyThreshold;
        }
        Log.i(this.TAG, 'update current latency threshold : ' + this._curLatencyThreshold);
    }

    _adjustDurationGap() {
        if (this._latencyAdjustCount < this._latencyDetectThreshold) {
            //一个统计周期内发生的追赶次数低于阈值，下调延迟阈值
            this._decreaseLatency(this._latencyDetectAdjustStep);

            // if(this._latencyAdjustCount == 0) {
            //     ++this._latencyNoAdjustCount;
            //     //如果连续5次统计周期都没有发生过追赶，则继续下调阈值5倍的this._latencyDetectAdjustStep
            //     if(this._latencyNoAdjustCount >= 5) {
            //         this._latencyNoAdjustCount = 0;
            //         this._decreaseLatency(this._latencyDetectAdjustStep * 5);
            //     }
            // }
        } else if (this._latencyAdjustCount > this._latencyDetectThreshold) {
            //一个统计周期内发生的追赶次数发生的比较多，则在即时调整的基础上，再继续增加5倍的this._latencyDetectAdjustStep
            this._increaseLatency(this._latencyDetectAdjustStep * 5);
        }
        this._latencyAdjustCount = 0;
    }

    _doDurationMonitor() {

        // Log.d(this.TAG, '_doDurationMonitor, decoded_frames:' + decoded_frames);
        if (!this._enableDurationMonitor || !this._mseSourceOpened) {
            return;
        }

        if (this._msectl && this._msectl.checkSourceBufferNull()) {
            return; 
        }

        let buffered = this._mediaElement.buffered;
        let currentTime = this._mediaElement.currentTime;
        let isPaused = this._mediaElement.paused;

        if (buffered == null || buffered.length == 0) {
            return;
        }

        let buffer_end = buffered.end(0);
        let current_play_delta = buffer_end - currentTime;

        // check duration
        if (!isPaused && current_play_delta > this._curLatencyThreshold) {
            //当前播放点与缓冲区末尾相差超过阈值，追赶
            let newGap = buffer_end - this._decreaseDurationStep;
            Log.w(this.TAG, 'high latency gap {' + current_play_delta 
                + '}, decrease ' + this._decreaseDurationStep + ' and set to ' + newGap);
            this._mediaElement.currentTime = newGap;
            this._requestSetTime = true;

            this._latencyAdjustCountTotal++;

            //一般来说，加载开始肯定会追赶，这里前2次追赶跳过，不进行延迟阈值调整
            if (this._latencyAdjustCountTotal > 2) {
                //调整到{延迟差 + this._latencyDetectAdjustStep}
                ++this._latencyAdjustCount;
                let newThreshold = current_play_delta + this._latencyDetectAdjustStep;
                this._updateCurrentLatencyThreshold(newThreshold);
            }
        }

        // check video frozen or not
        if (this._enableVideoFrozenMonitor) {

            // get current decoded frames count
            let decoded_frames = 0;
            if (this._mediaElement.getVideoPlaybackQuality) {
                let quality = this._mediaElement.getVideoPlaybackQuality();
                decoded_frames = quality.totalVideoFrames;
            } else if (this._mediaElement.webkitDecodedFrameCount != undefined) {
                decoded_frames = this._mediaElement.webkitDecodedFrameCount;
            } else {
                // could not get decoded frames count
                return;
            }
            
            if (this._lastDecodedFrames != 0) {
                //check whether video frozen or not
                if (this._lastDecodedFrames == decoded_frames) {
                    this._forzenTimes++;
                    // Log.w(this.TAG, '--== Video decoded frames stop increase, check count:' + this._forzenTimes);
                } else {
                    this._forzenTimes = 0;
                }
            }
            // Log.d(this.TAG, '--== decoded frame {last:' + this._lastDecodedFrames + ', cur:' + decoded_frames + '}');
            this._lastDecodedFrames = decoded_frames;

            if (this._forzenTimes >= this._frozenTimesThreshold) {
                // equals to threshold, emit VIDEO_FROZEN event
                Log.w(this.TAG, 'Report video frozen event');
                this._emitter.emit(PlayerEvents.VIDEO_FROZEN);
                this._forzenTimes = 0;
            }
        }
    }

    _onmseUpdateEnd() {
        if (!this._config.lazyLoad || this._config.isLive) {
            return;
        }

        let buffered = this._mediaElement.buffered;
        let currentTime = this._mediaElement.currentTime;
        let currentRangeStart = 0;
        let currentRangeEnd = 0;

        for (let i = 0; i < buffered.length; i++) {
            let start = buffered.start(i);
            let end = buffered.end(i);
            if (start <= currentTime && currentTime < end) {
                currentRangeStart = start;
                currentRangeEnd = end;
                break;
            }
        }

        if (currentRangeEnd >= currentTime + this._config.lazyLoadMaxDuration && this._progressChecker == null) {
            Log.v(this.TAG, 'Maximum buffering duration exceeded, suspend transmuxing task');
            this._suspendTransmuxer();
        }
    }

    _onmseBufferFull() {
        Log.v(this.TAG, 'MSE SourceBuffer is full, suspend transmuxing task');
        if (this._progressChecker == null) {
            this._suspendTransmuxer();
        }
        this._emitter.emit(PlayerEvents.MS_BUFFER_FULL);
    }

    _suspendTransmuxer() {
        if (this._transmuxer) {
            this._transmuxer.pause();

            if (this._progressChecker == null) {
                this._progressChecker = window.setInterval(this._checkProgressAndResume.bind(this), 1000);
            }
        }
    }

    _checkProgressAndResume() {
        let currentTime = this._mediaElement.currentTime;
        let buffered = this._mediaElement.buffered;

        let needResume = false;

        for (let i = 0; i < buffered.length; i++) {
            let from = buffered.start(i);
            let to = buffered.end(i);
            if (currentTime >= from && currentTime < to) {
                if (currentTime >= to - this._config.lazyLoadRecoverDuration) {
                    needResume = true;
                }
                break;
            }
        }

        if (needResume) {
            window.clearInterval(this._progressChecker);
            this._progressChecker = null;
            if (needResume) {
                Log.v(this.TAG, 'Continue loading from paused position');
                this._transmuxer.resume();
            }
        }
    }

    _isTimepointBuffered(seconds) {
        let buffered = this._mediaElement.buffered;

        for (let i = 0; i < buffered.length; i++) {
            let from = buffered.start(i);
            let to = buffered.end(i);
            if (seconds >= from && seconds < to) {
                return true;
            }
        }
        return false;
    }

    _internalSeek(seconds) {
        let directSeek = this._isTimepointBuffered(seconds);

        let directSeekBegin = false;
        let directSeekBeginTime = 0;

        if (seconds < 1.0 && this._mediaElement.buffered.length > 0) {
            let videoBeginTime = this._mediaElement.buffered.start(0);
            if ((videoBeginTime < 1.0 && seconds < videoBeginTime) || Browser.safari) {
                directSeekBegin = true;
                // also workaround for Safari: Seek to 0 may cause video stuck, use 0.1 to avoid
                directSeekBeginTime = Browser.safari ? 0.1 : videoBeginTime;
            }
        }

        if (directSeekBegin) {  // seek to video begin, set currentTime directly if beginPTS buffered
            this._requestSetTime = true;
            this._mediaElement.currentTime = directSeekBeginTime;
        }  else if (directSeek) {  // buffered position
            if (!this._alwaysSeekKeyframe) {
                this._requestSetTime = true;
                this._mediaElement.currentTime = seconds;
            } else {
                let idr = this._msectl.getNearestKeyframe(Math.floor(seconds * 1000));
                this._requestSetTime = true;
                if (idr != null) {
                    this._mediaElement.currentTime = idr.dts / 1000;
                } else {
                    this._mediaElement.currentTime = seconds;
                }
            }
            if (this._progressChecker != null) {
                this._checkProgressAndResume();
            }
        } else {
            if (this._progressChecker != null) {
                window.clearInterval(this._progressChecker);
                this._progressChecker = null;
            }
            this._msectl.seek(seconds);
            this._transmuxer.seek(Math.floor(seconds * 1000));  // in milliseconds
            // no need to set mediaElement.currentTime if non-accurateSeek,
            // just wait for the recommend_seekpoint callback
            if (this._config.accurateSeek) {
                this._requestSetTime = true;
                this._mediaElement.currentTime = seconds;
            }
        }
    }

    _checkAndApplyUnbufferedSeekpoint() {
        if (this._seekpointRecord) {
            if (this._seekpointRecord.recordTime <= this._now() - 100) {
                let target = this._mediaElement.currentTime;
                this._seekpointRecord = null;
                if (!this._isTimepointBuffered(target)) {
                    if (this._progressChecker != null) {
                        window.clearTimeout(this._progressChecker);
                        this._progressChecker = null;
                    }
                    // .currentTime is consists with .buffered timestamp
                    // Chrome/Edge use DTS, while FireFox/Safari use PTS
                    this._msectl.seek(target);
                    this._transmuxer.seek(Math.floor(target * 1000));
                    // set currentTime if accurateSeek, or wait for recommend_seekpoint callback
                    if (this._config.accurateSeek) {
                        this._requestSetTime = true;
                        this._mediaElement.currentTime = target;
                    }
                }
            } else {
                window.setTimeout(this._checkAndApplyUnbufferedSeekpoint.bind(this), 50);
            }
        }
    }

    _checkAndResumeStuckPlayback(stalled) {
        let media = this._mediaElement;
        if (stalled || !this._receivedCanPlay || media.readyState < 2) {  // HAVE_CURRENT_DATA
            let buffered = media.buffered;
            if (buffered.length > 0 && media.currentTime < buffered.start(0)) {
                Log.w(this.TAG, `Playback seems stuck at ${media.currentTime}, seek to ${buffered.start(0)}`);
                this._requestSetTime = true;
                this._mediaElement.currentTime = buffered.start(0);
                this._mediaElement.removeEventListener('progress', this.e.onvProgress);
            }
        } else {
            // Playback didn't stuck, remove progress event listener
            this._mediaElement.removeEventListener('progress', this.e.onvProgress);
        }
    }

    _onvLoadedMetadata(e) {
        if (this._pendingSeekTime != null) {
            this._mediaElement.currentTime = this._pendingSeekTime;
            this._pendingSeekTime = null;
        }
    }

    _onvSeeking(e) {  // handle seeking request from browser's progress bar
        let target = this._mediaElement.currentTime;
        let buffered = this._mediaElement.buffered;

        if (this._requestSetTime) {
            this._requestSetTime = false;
            return;
        }

        if (target < 1.0 && buffered.length > 0) {
            // seek to video begin, set currentTime directly if beginPTS buffered
            let videoBeginTime = buffered.start(0);
            if ((videoBeginTime < 1.0 && target < videoBeginTime) || Browser.safari) {
                this._requestSetTime = true;
                // also workaround for Safari: Seek to 0 may cause video stuck, use 0.1 to avoid
                this._mediaElement.currentTime = Browser.safari ? 0.1 : videoBeginTime;
                return;
            }
        }

        if (this._isTimepointBuffered(target)) {
            if (this._alwaysSeekKeyframe) {
                let idr = this._msectl.getNearestKeyframe(Math.floor(target * 1000));
                if (idr != null) {
                    this._requestSetTime = true;
                    this._mediaElement.currentTime = idr.dts / 1000;
                }
            }
            if (this._progressChecker != null) {
                this._checkProgressAndResume();
            }
            return;
        }

        this._seekpointRecord = {
            seekPoint: target,
            recordTime: this._now()
        };
        window.setTimeout(this._checkAndApplyUnbufferedSeekpoint.bind(this), 50);
    }

    _onvCanPlay(e) {
        this._receivedCanPlay = true;
        this._mediaElement.removeEventListener('canplay', this.e.onvCanPlay);

        if (this._enableAudioGain && g_audioSource == undefined) {
            let media = this._mediaElement;
            g_audioSource = g_audioCtx.createMediaElementSource(media);
        }
        g_audioSource.connect(g_audioGain);
        Log.d(this.TAG, 'Enable audio gain, level = ' + this._audioGainLevel);
    }

    _onvStalled(e) {
        this._checkAndResumeStuckPlayback(true);
    }

    _onvProgress(e) {
        this._checkAndResumeStuckPlayback();
    }

}

export default FlvPlayer;